import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { NzPopoverModule } from 'ng-zorro-antd/popover'
import { NzSpinModule } from 'ng-zorro-antd/spin'
import { ICalendarLevel } from './calendar-level'
import { ICalendarSelection } from './calendar-selection'
import { CalendarService } from './calendar.service'
import { CalendarTooltipTableComponent } from './calendar-tooltip-table/calendar-tooltip-table.component'

@Component({
    selector: 'app-calendar-filter',
    templateUrl: './calendar-filter.component.html',
    styleUrls: ['./calendar-filter.component.scss'],
    standalone: true,
    imports: [CommonModule, NzPopoverModule, NzSpinModule, CalendarTooltipTableComponent],
})
export class CalendarFilterComponent implements OnInit {
  @Input() showTooltip: boolean = true
  @Input() cssClassCallback: any
  @Input() cssStyleCallback: any
  @Input() selection: ICalendarSelection
  @Input() useDefault: boolean = false
  @Input() loading: boolean = false
  @Input() months: ICalendarLevel[] = []
  @Input() days: ICalendarLevel[]
  @Input() hours: ICalendarLevel[]
  @Output() changeCalendarEvent: EventEmitter<ICalendarSelection> = new EventEmitter<
    ICalendarSelection
  >()

  private _year: number
  private _month: number
  private _day: number
  private _hour: number
  private _noOfDays: number = 31

  public get noOfDays(): number {
    return this.days ? this.days.length : this._noOfDays
  }

  public get year(): number {
    return this._year
  }

  public get month(): number {
    return this._month
  }

  public get day(): number {
    return this._day
  }

  public get hour(): number {
    return this._hour
  }

  constructor(private service: CalendarService) {}

  public onSelectMonth(value: ICalendarLevel): void {
    if (this.loading) {
      return
    }
    this._year = value.level.year
    this._month = value.level.month
    this._day = undefined
    this._hour = undefined
    if (this.useDefault) {
      this.days = this.service.getDefaultDaysLevel(this._year, this._month)
    }
    this.changeCalendarEvent.emit({ year: this._year, month: this._month })
    return
  }

  public onSelectDay(value: ICalendarLevel): void {
    if (this.loading) {
      return
    }
    this._day = value.level.day
    this._hour = undefined
    if (this.useDefault) {
      this.hours = this.service.getDefaultHoursLevel(this._year, this._month, this._day)
    }
    this.changeCalendarEvent.emit({ year: this._year, month: this._month, day: this._day })
    return
  }

  public onSelectHour(value: ICalendarLevel): void {
    if (this.loading) {
      return
    }
    this._hour = value.level.hour
    this.changeCalendarEvent.emit({
      year: this._year,
      month: this._month,
      day: this._day,
      hour: this._hour,
    })
    return
  }

  private parseSelection(): void {
    this._year = this.selection && this.selection.year ? this.selection.year : undefined
    this._month = this.selection && this.selection.month ? this.selection.month : undefined
    this._day = this.selection && this.selection.day ? this.selection.day : undefined
    this._hour = this.selection && this.selection.hour ? this.selection.hour : undefined
  }

  public isActive(data: ICalendarLevel, type: 'M' | 'D' | 'H'): boolean {
    if (type === 'M') {
      return this.isActiveMonth(data)
    }
    if (type === 'D') {
      return this.isActiveDay(data)
    }
    if (type === 'H') {
      return this.isActiveHour(data)
    }
    return false
  }

  public isActiveMonth(data: ICalendarLevel): boolean {
    return (
      !this.loading &&
      this.selection &&
      this.selection.year === data.level.year &&
      this.selection.month === data.level.month
    )
  }

  public isActiveDay(data: ICalendarLevel): boolean {
    return (
      !this.loading &&
      this.selection &&
      this.selection.day === data.level.day
    )
  }

  public isActiveHour(data: ICalendarLevel): boolean {
    return (
      !this.loading &&
      this.selection &&
      this.selection.hour === data.level.hour
    )
  }

  public getCssClass(data: ICalendarLevel, type: 'M' | 'D' | 'H'): any {
    let classes: any = {
      active: this.isActive(data, type),
      disabled: this.loading,
    }

    const cssClass: string | null = this.getCssClassByCallback(data)
    if (cssClass) {
      classes[cssClass] = true
    }

    return classes
  }

  public getCssClassByCallback(data: ICalendarLevel): string | null {
    if (!this.loading && this.cssClassCallback) {
      return this.cssClassCallback(data.status)
    }

    return null
  }

  public getCssStyle(data: ICalendarLevel): any {
    const cssStyle: any = this.getCssStyleByCallback(data)
    if (cssStyle) {
      return cssStyle
    }

    return null
  }

  public getCssStyleByCallback(data: ICalendarLevel): any {
    if (!this.loading && this.cssStyleCallback) {
      return this.cssStyleCallback(data.status)
    }

    return null
  }

  public hasTooltipData(item: ICalendarLevel): boolean {
    if (!item.extra || !item.extra.tooltip) {
      return false
    }
    for (let key of Object.keys(item.extra.tooltip)) {
      if (item.extra.tooltip[key].tags && item.extra.tooltip[key].tags.length > 0) {
        return true
      }
    }

    return false
  }

  public ngOnInit(): void {
    if (this.useDefault) {
      this.months = this.service.getDefaultMonthsLevel()
    }
    this.parseSelection()
  }
}
